import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { httpsCallable, type HttpsCallableResult } from 'firebase/functions';
import { functions } from '../lib/firebase';

/**
 * Team Activity - Lodgements data access.
 *
 * Everything goes through callables, never a direct Firestore read, because
 * the row-level scoping lives server-side: a broker asking for the whole
 * company still receives only their own rows. A client-side query could not
 * enforce that without trusting the caller.
 */

export type LodgementStatus =
  | 'NewLeads'
  | 'Researching'
  | 'PreApproved'
  | 'ConditionallyApproved'
  | 'UnconditionallyApproved'
  | 'Lodged'
  | 'InProgress'
  | 'PendingSettlement'
  | 'Settled'
  | 'Withdrawn'
  | 'Cancelled';

export interface MirEntry {
  category: string;
  reason: string;
  raisedOn?: string | null;
  resolvedOn?: string | null;
}

/** What the user types. Derived + settlement fields are server-owned. */
export interface LodgementInput {
  clientName: string;
  /**
   * Namespaced owner ref (`user:` / `analyst:` / `broker:`), or ABSENT to mean
   * "the caller's own work". Optional on purpose: the server resolves an
   * omitted owner to the caller's primary ref, and only a role that may write
   * any owner is allowed to name one. Shape and helpers:
   * `packages/shared-schemas/src/ownerRef.ts`.
   */
  ownerId?: string;
  assistantId?: string | null;
  dateLodged: string;
  dateApproved?: string | null;
  lender: string;
  lodgeAmountCents: number;
  status: LodgementStatus;
  due?: string | null;
  mirs: MirEntry[];
  oneTouch: boolean;
  googleReviewAsked: boolean;
  googleReviewReceived: boolean;
  structure?: string | null;
  remarks?: string | null;
  customFields: Record<string, string | number | boolean | null>;
}

export interface LodgementRecord extends LodgementInput {
  id: string;
  companyId: string;
  /** Always present on a stored row: the server resolved it on write. */
  ownerId: string;
  daysToFormal: number | null;
  mirCount: number;
  settledDate: string | null;
  settledAmountCents: number | null;
  settlementSource: string | null;
  matchedLoanId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ListResult {
  lodgements: LodgementRecord[];
  /** 'own' when the server narrowed the query to the caller's rows. */
  scope: 'company' | 'own';
  count: number;
  /** Opaque; feed back as `cursor` for the next page. Null = last page. */
  nextCursor: string | null;
}

const TERMINAL: LodgementStatus[] = ['Settled', 'Withdrawn', 'Cancelled'];

export interface LodgementStats {
  lodgementCount: number;
  lodgedAmountCents: number;
  settledCount: number;
  settledAmountCents: number;
  conversionPct: number | null;
  mirRatePct: number | null;
  medianDaysToFormal: number | null;
}

/** Derived once, here, so no component recomputes them differently. */
export function computeStats(rows: LodgementRecord[]): LodgementStats {
  const settled = rows.filter((r) => r.settledDate !== null || r.status === 'Settled');
  const withMir = rows.filter((r) => r.mirCount > 0);
  const formals = rows
    .map((r) => r.daysToFormal)
    .filter((d): d is number => typeof d === 'number' && d >= 0)
    .sort((a, b) => a - b);
  const median =
    formals.length === 0
      ? null
      : formals.length % 2
        ? formals[(formals.length - 1) / 2]
        : Math.round((formals[formals.length / 2 - 1] + formals[formals.length / 2]) / 2);
  return {
    lodgementCount: rows.length,
    lodgedAmountCents: rows.reduce((a, r) => a + (r.lodgeAmountCents || 0), 0),
    settledCount: settled.length,
    settledAmountCents: settled.reduce((a, r) => a + (r.settledAmountCents || 0), 0),
    conversionPct: rows.length ? Math.round((settled.length / rows.length) * 100) : null,
    mirRatePct: rows.length ? Math.round((withMir.length / rows.length) * 100) : null,
    medianDaysToFormal: median,
  };
}

export const isTerminal = (s: LodgementStatus): boolean => TERMINAL.includes(s);

interface Options {
  /** Inclusive ISO date bounds on dateLodged. A range, not a bucket. */
  from?: string;
  to?: string;
  ownerId?: string;
}

/**
 * The window is fetched at the callable's MAXIMUM page size.
 *
 * WHY THERE IS NO READER-FACING SERVER PAGER ANY MORE. The register sorts,
 * filters and paginates in the browser, and all three of those are wrong unless
 * they see every row in the period: a "sort by amount" that only ordered the
 * rows already downloaded would put the largest deal on page four. So the hook
 * owns the paging, the window arrives complete, and the reader's page size is a
 * presentation choice made downstream.
 */
const FETCH_PAGE_SIZE = 500;

/**
 * Hard ceiling on the pass: 10 pages, so 5000 rows.
 *
 * A loop over an unbounded cursor is one bad filter away from pulling a tenant's
 * entire history into a browser tab. The cap is what stops that, and the price
 * of the cap is that the caller MUST be told when it bites: `rowsComplete` goes
 * false and the consumer is expected to say so on screen. Silently summarising
 * a subset as though it were the whole period is the failure this guards.
 */
const FETCH_MAX_PAGES = 10;

/** The reconciler's report. Shape mirrors reconcileLodgementSettlements.ts. */
export interface ReconcileReport {
  dryRun: boolean;
  candidatesConsidered?: number;
  summary: {
    total: number;
    matched: number;
    matchedByName: number;
    matchedByCorroboration: number;
    unmatched: Record<string, number>;
  };
  skippedManualOverride: number;
  skippedAlreadySettled: number;
  written: number;
}

/**
 * OMIT absent keys rather than passing undefined. The callable SDK encodes a
 * property whose value is undefined as `null` on the wire, so
 * `{ from: undefined }` arrives server-side as `{ from: null }`. The backend
 * tolerates that for the date bounds (callableParam.ts), but an `ownerId: null`
 * fails the owner-ref regex outright, and omitting the key is what tells the
 * server "attribute this to the caller".
 */
function listPayload(
  companyId: string,
  opts: Options,
  cursor: string | null,
  pageSize: number = FETCH_PAGE_SIZE
): Record<string, unknown> {
  return {
    companyId,
    pageSize,
    ...(opts.from ? { from: opts.from } : {}),
    ...(opts.to ? { to: opts.to } : {}),
    ...(opts.ownerId ? { ownerId: opts.ownerId } : {}),
    ...(cursor ? { cursor } : {}),
  };
}

/**
 * Same rule for the write paths: an absent owner is an absent key.
 *
 * On UPDATE this is not a nicety, it is the contract. An update that carries no
 * ownerId keeps the stored one verbatim (lodgementCallables.ts), whereas one
 * that names an owner is a REATTRIBUTION and is re-resolved and re-probed. So
 * an unchanged owner must never be echoed back: doing so would make a row
 * uneditable the moment its broker record was merged away, which is precisely
 * when someone needs to correct it. Callers pass ownerId only when the user
 * actively moved the picker.
 */
function writePayload(lodgement: LodgementInput): Record<string, unknown> {
  const { ownerId, ...rest } = lodgement;
  return ownerId ? { ...rest, ownerId } : rest;
}

export function useLodgements(companyId: string | undefined, opts: Options = {}) {
  const [rows, setRows] = useState<LodgementRecord[]>([]);
  const [scope, setScope] = useState<'company' | 'own'>('own');
  /** First page in flight: nothing to render yet. */
  const [loading, setLoading] = useState<boolean>(Boolean(companyId));
  /** Later pages in flight: rows are on screen but the window is not whole. */
  const [loadingRest, setLoadingRest] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const { from, to, ownerId } = opts;

  // Generation counter, not a per-effect `cancelled` flag: the pass awaits
  // between pages, and folding a late page into a set the filters have already
  // moved on from would silently mix two different queries in one table.
  const generation = useRef(0);

  /**
   * Page the whole window, sequentially, because the cursor is opaque and each
   * page names the next.
   *
   * The first page is published as soon as it lands so the table paints at
   * once; the rest are accumulated and published together at the end. Setting
   * state on every page would re-sort and re-paginate the register under the
   * reader's cursor, which is worse than a short wait with the count still
   * settling.
   */
  useEffect(() => {
    if (!companyId) {
      setRows([]);
      setLoading(false);
      setLoadingRest(false);
      setComplete(false);
      return;
    }
    const gen = (generation.current += 1);
    setLoading(true);
    setLoadingRest(false);
    setError(null);
    // Reset here: a filter change starts a new result set, so the old rows must
    // not linger while the first page is in flight.
    setRows([]);
    setComplete(false);

    const call = httpsCallable<Record<string, unknown>, ListResult>(functions, 'listLodgements');
    void (async () => {
      const acc: LodgementRecord[] = [];
      let next: string | null = null;
      let reachedEnd = false;
      try {
        for (let page = 0; page < FETCH_MAX_PAGES; page++) {
          // Annotated rather than inferred: `next` is both an input to this
          // call and assigned from its result, and TypeScript reads that as a
          // circular initializer (TS7022) unless the result type is stated.
          const res: HttpsCallableResult<ListResult> = await call(
            listPayload(companyId, { from, to, ownerId }, next)
          );
          if (gen !== generation.current) return;
          acc.push(...(res.data?.lodgements ?? []));
          next = res.data?.nextCursor ?? null;
          if (page === 0) {
            setScope(res.data?.scope ?? 'own');
            setRows([...acc]);
            setLoading(false);
            setLoadingRest(next !== null);
          }
          if (!next) {
            reachedEnd = true;
            break;
          }
        }
        if (gen !== generation.current) return;
        setRows(acc);
        // False here means the cap bit before the data ran out. The consumer
        // has to say so on screen; understating a total in silence is the one
        // outcome this whole mechanism exists to prevent.
        setComplete(reachedEnd);
      } catch (e: unknown) {
        if (gen !== generation.current) return;
        // Surface the failure. A silent catch here would render an empty table
        // that looks like "no lodgements yet" when it is actually a denied or
        // broken read.
        const msg = e instanceof Error ? e.message : 'Failed to load lodgements';
        console.error('[useLodgements] paging failed', e);
        setError(msg);
        // Partial rows are still worth showing, but only alongside the
        // incomplete flag, never as a finished total.
        setRows(acc);
        setComplete(false);
      } finally {
        if (gen === generation.current) {
          setLoading(false);
          setLoadingRest(false);
        }
      }
    })();
  }, [companyId, from, to, ownerId, refreshKey]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const create = useCallback(
    async (lodgement: LodgementInput) => {
      if (!companyId) throw new Error('No company');
      const call = httpsCallable<Record<string, unknown>, { id: string }>(functions, 'createLodgement');
      const res = await call({ companyId, lodgement: writePayload(lodgement) });
      refresh();
      return res.data.id;
    },
    [companyId, refresh]
  );

  /**
   * Edit an existing row.
   *
   * `lodgement` is the FULL input, matching the callable's schema, which
   * validates the whole shape rather than a partial patch. Ownership follows
   * writePayload's rule: include ownerId only to reattribute, omit it in every
   * other case so the server keeps the stored owner untouched.
   */
  const update = useCallback(
    async (id: string, lodgement: LodgementInput) => {
      if (!companyId) throw new Error('No company');
      const call = httpsCallable<Record<string, unknown>, { id: string }>(functions, 'updateLodgement');
      await call({ companyId, id, lodgement: writePayload(lodgement) });
      refresh();
    },
    [companyId, refresh]
  );

  /**
   * Attach settlements from the commission pipeline to unsettled lodgements.
   *
   * `commit: true` on purpose. The callable defaults to a dry run, which is the
   * right default for a script but useless behind a button whose whole point is
   * that the Settled columns fill in; a dry run would report matches and change
   * nothing. The server still refuses to overwrite a manual override or an
   * already-settled row, so the write is additive rather than destructive.
   *
   * Admin-only server-side (enforceRole super_admin / administrator). The UI
   * mirrors that gate rather than relying on it.
   */
  const reconcileSettlements = useCallback(async (): Promise<ReconcileReport> => {
    if (!companyId) throw new Error('No company');
    const call = httpsCallable<Record<string, unknown>, ReconcileReport>(
      functions,
      'reconcileLodgementSettlements'
    );
    const res = await call({ companyId, commit: true });
    refresh();
    return res.data;
  }, [companyId, refresh]);

  const stats = useMemo(() => computeStats(rows), [rows]);

  return {
    /** Every row in the window, subject to `rowsComplete`. */
    rows,
    stats,
    scope,
    loading,
    /** True while pages after the first are still arriving. */
    loadingRest,
    error,
    refresh,
    create,
    update,
    reconcileSettlements,
    /** True only when the pass reached the end of the window. */
    rowsComplete: complete,
  };
}
